/**
 * The signed-in session: a bearer token in localStorage plus the Account it
 * belongs to, in a small zustand store (zustand is already a dependency;
 * see lib/mock/store.ts for the same pattern the demo apparatus uses).
 *
 * Deliberately NOT a cookie: cloud-api's CORS posture (middleware/cors.ts)
 * never sets `credentials: true`, so auth here is a bearer header the same
 * way a personal API token works: see that file's header for the tradeoff
 * (an XSS in this dashboard is a session compromise, same as a leaked PAT).
 */
import { create } from 'zustand';
import type { Account, Project } from '../types';
import { resetUser } from '../analytics/posthog';

const STORAGE_KEY = 'sv-cloud-session';
/**
 * Stashes the ADMIN's own token+email while a God View "view as" session is
 * active in the normal slot above. See startImpersonation/stopImpersonation.
 * Its mere presence is also how isImpersonating() answers synchronously,
 * without reading the (possibly stale) zustand store.
 */
const ADMIN_STASH_KEY = 'sv-cloud-admin-session';

export function getToken(): string | null {
  return localStorage.getItem(STORAGE_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(STORAGE_KEY, token);
}

export function clearSession(): void {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(ADMIN_STASH_KEY);
  useSessionStore.setState({ account: undefined, projects: [], status: 'signed-out', impersonation: null });
  resetUser();
}

interface AdminStash {
  token: string;
  email: string;
  expiresAt: string;
}

function readAdminStash(): AdminStash | null {
  const raw = localStorage.getItem(ADMIN_STASH_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AdminStash;
  } catch {
    return null;
  }
}

export function isImpersonating(): boolean {
  return readAdminStash() !== null;
}

/**
 * God View "view as" (screens/admin/GodView.tsx): swaps the active session
 * token for a short-lived one scoped to the target account, stashing the
 * admin's own token so stopImpersonation() can restore it. Setting status
 * back to 'loading' is deliberate: it's what makes useAuthInit's existing
 * effect (it only runs `if (status !== 'loading') return`) re-fetch and
 * re-hydrate the account/projects as the impersonated user, with no new
 * fetch logic needed.
 */
export function startImpersonation(
  newToken: string,
  adminToken: string,
  adminEmail: string,
  expiresAt: string,
): void {
  const stash: AdminStash = { token: adminToken, email: adminEmail, expiresAt };
  localStorage.setItem(ADMIN_STASH_KEY, JSON.stringify(stash));
  setToken(newToken);
  useSessionStore.setState({ status: 'loading', impersonation: { adminEmail, expiresAt } });
}

/** Reverses startImpersonation, restoring the admin's own session. Same re-hydrate trick, now as the admin. */
export function stopImpersonation(): void {
  const stash = readAdminStash();
  if (!stash) return;
  localStorage.removeItem(ADMIN_STASH_KEY);
  setToken(stash.token);
  useSessionStore.setState({ status: 'loading', impersonation: null });
}

export type SessionStatus = 'loading' | 'signed-in' | 'signed-out';

interface ImpersonationState {
  adminEmail: string;
  expiresAt: string;
}

interface SessionStore {
  status: SessionStatus;
  account: Account | undefined;
  /**
   * The real project list, loaded once alongside the account (AppShell's
   * auth-guard effect) so the chrome, top bar, rail, command palette, has
   * something to read that isn't the demo store's fixtures. Screens that
   * need a fresher view (ProjectsList, Overview) keep calling listProjects()
   * directly; this is the shell's own copy, not the source of truth.
   */
  projects: Project[];
  /** Non-null while a God View "view as" session is active. See startImpersonation(). */
  impersonation: ImpersonationState | null;
  setAccount: (account: Account) => void;
  setProjects: (projects: Project[]) => void;
  /**
   * Fold one freshly-read project into the shell's copy.
   *
   * The list above is loaded once at sign-in, which was fine while the rail
   * only printed a name, but the rail now hides destinations while a
   * project is provisioning (ProjectNav), and a copy that never updates
   * would keep them hidden for the rest of the session after the run
   * settles. Every screen that reads one project through getProject() calls
   * this, so the shell follows what the screen already knows.
   */
  upsertProject: (project: Project) => void;
  setStatus: (status: SessionStatus) => void;
}

export const useSessionStore = create<SessionStore>((set) => {
  const stash = readAdminStash();
  return {
    status: getToken() ? 'loading' : 'signed-out',
    account: undefined,
    projects: [],
    impersonation: stash ? { adminEmail: stash.email, expiresAt: stash.expiresAt } : null,
    setAccount: (account) => set({ account, status: 'signed-in' }),
    setProjects: (projects) => set({ projects }),
    upsertProject: (project) =>
      set((state) => {
        const index = state.projects.findIndex((p) => p.id === project.id);
        if (index === -1) return { projects: [...state.projects, project] };
        const next = [...state.projects];
        next[index] = project;
        return { projects: next };
      }),
    setStatus: (status) => set({ status }),
  };
});
